/**
 * SaveProgressCard — floating overlay that narrates the Save button's
 * whole-document sweep (prepare → read → save → update issues → done).
 *
 * The Save flow reads the live Word content of every section, compares it to
 * the Mongo SSOT, and persists the changed ones. On a large draft that is a
 * multi-second operation; before this card the writer saw only the Save
 * button's "Saving…" label with no sense of progress. This wraps the shared
 * ``HomeLoadProgress`` stepper in a small fixed-position card anchored near the
 * toolbar so the wait is legible without shoving the layout around.
 *
 * Pure presentation — the parent (VariantCShell) owns the step model and
 * advances it across ``handleSave``'s phases.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { HomeLoadProgress, type LoadStep } from "./HomeLoadProgress";

interface SaveProgressCardProps {
  /** Ordered Save steps with their current state (from ``buildSaveSteps``). */
  steps: LoadStep[];
  /** Non-null when the save failed — renders the error frame + dismiss. */
  error?: string | null;
  /** Terminal success frame ("Saved ✓"), shown briefly before auto-dismiss. */
  done?: boolean;
  /** Dismiss the card (error path — writer acks and returns to editing). */
  onDismiss?: () => void;
  /** 0–100 determinate progress driving the bar under the steps. */
  percent?: number | null;
}

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    top: "10px",
    right: "10px",
    zIndex: 1000,
    width: "260px",
    maxWidth: "calc(100vw - 20px)",
    // The card itself carries the panel styling; this just positions it.
    filter: "drop-shadow(0 4px 12px rgba(76,51,138,0.14))",
  },
});

export function SaveProgressCard({ steps, error, done, onDismiss, percent }: SaveProgressCardProps): React.ReactElement {
  const styles = useStyles();
  return (
    <div className={styles.overlay}>
      <HomeLoadProgress
        steps={steps}
        error={error}
        done={done}
        header="Saving your work"
        errorHeader="Save failed"
        doneHeader="Saved"
        dismissLabel="Dismiss"
        onDismiss={onDismiss}
        percent={percent}
      />
    </div>
  );
}
